import { SEMANTIC_COLORS } from '../design-system/colors';

/**
 * Calculate confidence rank based on expected lift
 * @param lift - Expected lift percentage
 * @returns Confidence rank (1=Low, 2=Medium, 3=High)
 */
export const calculateConfidenceRank = (lift: number): 1 | 2 | 3 => {
  return lift >= 5 ? 3 : lift < 1 ? 1 : 2;
};

/**
 * Calculate ICE score (Impact, Confidence, Ease)
 * @param confidence - Confidence rank (1-3)
 * @param effort - Effort level (1-5)
 * @param expectedLift - Expected lift percentage
 * @returns ICE score (0-1 scale)
 */
export const calculateICEScore = (
  confidence: number,
  effort: number,
  expectedLift: number
): number => {
  // Normalize confidence (1-3 scale, higher is better)
  const normalizedConfidence = confidence / 3;

  // Normalize effort (1-5 scale, lower is better, so invert)
  const normalizedEffort = effort === 0 ? 0 : (6 - effort) / 5;

  // Normalize expected lift (0-20+ scale, higher is better)
  const normalizedLift = Math.min(expectedLift / 20, 1);

  // Calculate ICE score (0-1 scale)
  return (normalizedConfidence + normalizedEffort + normalizedLift) / 3;
};

/**
 * Get score color class based on score value
 * @param score - Score value (0-100)
 * @returns Tailwind color class
 */
export const getScoreColor = (score: number): string => {
  return score >= 90
    ? SEMANTIC_COLORS.score.high
    : score >= 50
    ? SEMANTIC_COLORS.score.medium
    : SEMANTIC_COLORS.score.low;
};

/**
 * Get confidence text and color
 * @param rank - Confidence rank (1-3)
 * @returns Object with text and color class
 */
export const getConfidenceDisplay = (rank: 1 | 2 | 3): { text: string; color: string } => {
  const displays = {
    1: { text: 'Low', color: SEMANTIC_COLORS.confidence.low },
    2: { text: 'Medium', color: SEMANTIC_COLORS.confidence.medium },
    3: { text: 'High', color: SEMANTIC_COLORS.confidence.high }
  };

  return displays[rank];
};

/**
 * Get effort text and color
 * @param effort - Effort level (1-5)
 * @returns Object with text and color class
 */
export const getEffortDisplay = (effort: 1 | 2 | 3 | 4 | 5): { text: string; color: string } => {
  const texts = ['Very Easy', 'Easy', 'Medium', 'Hard', 'Very Hard'];
  const text = texts[effort - 1] || 'Medium';

  const color = effort <= 2
    ? SEMANTIC_COLORS.effort.easy
    : effort <= 3
    ? SEMANTIC_COLORS.effort.medium
    : SEMANTIC_COLORS.effort.hard;

  return { text, color };
};
